// A company's public page at <company>.<root domain>: who they are, what they do, how to reach them,
// and a way in for their staff and customers. Content comes from the company's own website settings.
import { ArrowRight, Globe, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Link } from "lib/router";

const isHex = (c) => /^#[0-9a-fA-F]{6}$/.test(c || "");

export default function Microsite({ name, site }) {
  const accent = isHex(site?.accent_color) ? site.accent_color : "#5b5bd6";
  const services = Array.isArray(site?.services) ? site.services.filter((s) => s?.title) : [];
  const contacts = [
    site?.contact_phone && { Icon: Phone, label: site.contact_phone, href: `tel:${site.contact_phone.replace(/\s+/g, "")}` },
    site?.whatsapp && { Icon: MessageCircle, label: `WhatsApp ${site.whatsapp}`, href: `https://wa.me/${site.whatsapp.replace(/[^\d]/g, "")}` },
    site?.contact_email && { Icon: Mail, label: site.contact_email, href: `mailto:${site.contact_email}` },
    site?.website && { Icon: Globe, label: site.website.replace(/^https?:\/\//, ""), href: site.website },
    site?.address && { Icon: MapPin, label: site.address },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-bg text-text">
      <header className="border-b border-line bg-panel">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3 sm:px-6">
          {site?.logo_url ? (
            <img src={site.logo_url} alt={name} className="h-9 w-auto max-w-[160px] object-contain" />
          ) : (
            <span className="grid size-9 place-items-center rounded-lg text-[15px] font-bold text-white" style={{ background: accent }}>
              {String(name || "?").slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className="text-[15px] font-semibold text-fg">{name}</span>
          <Link
            to="/login"
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[13.5px] font-medium text-white no-underline"
            style={{ background: accent }}
          >
            Sign in <ArrowRight className="size-4" />
          </Link>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 md:py-20">
          <h1 className="m-0 max-w-3xl text-[30px] leading-[1.15] font-semibold tracking-[-0.025em] text-fg md:text-[42px]">
            {site?.headline || name}
          </h1>
          {site?.about && <p className="m-0 mt-5 max-w-2xl text-[16px] leading-relaxed whitespace-pre-line text-muted">{site.about}</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login" className="inline-flex h-11 items-center gap-2 rounded-lg px-5 text-[14.5px] font-medium text-white no-underline" style={{ background: accent }}>
              Sign in to your account <ArrowRight className="size-[18px]" />
            </Link>
            {contacts[0]?.href && (
              <a href={contacts[0].href} className="inline-flex h-11 items-center gap-2 rounded-lg border border-line bg-panel px-5 text-[14.5px] font-medium text-fg no-underline">
                Contact us
              </a>
            )}
          </div>
        </section>

        {services.length > 0 && (
          <section className="border-t border-line bg-panel">
            <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
              <h2 className="m-0 text-[20px] font-semibold text-fg">What we do</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {services.map((s, i) => (
                  <div key={i} className="rounded-[var(--radius-card)] border border-line p-5">
                    <span className="block h-1 w-8 rounded-full" style={{ background: accent }} />
                    <h3 className="m-0 mt-4 text-[15px] font-semibold text-fg">{s.title}</h3>
                    {s.text && <p className="m-0 mt-1.5 text-[13.5px] leading-relaxed text-muted">{s.text}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {contacts.length > 0 && (
          <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
            <h2 className="m-0 text-[20px] font-semibold text-fg">Contact</h2>
            <ul className="m-0 mt-5 grid list-none gap-3 p-0 sm:grid-cols-2">
              {contacts.map(({ Icon, label, href }) => (
                <li key={label} className="flex items-start gap-3 rounded-lg border border-line bg-panel px-4 py-3 text-[14px]">
                  <Icon className="mt-0.5 size-[18px] shrink-0" style={{ color: accent }} />
                  {href ? (
                    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-fg no-underline hover:underline">
                      {label}
                    </a>
                  ) : (
                    <span className="text-fg">{label}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-6 text-[12.5px] text-faint sm:px-6">
          <span>© {new Date().getFullYear()} {name}</span>
          <span>Runs on Qayd</span>
        </div>
      </footer>
    </div>
  );
}
